import React from 'react';
import { Helmet } from 'react-helmet';
import { Link, useParams, Navigate } from 'react-router-dom';
import Header from '../components/Header';
import Breadcrumbs from '../components/Breadcrumbs';
import DOMPurify from 'dompurify';
import Footer from '../components/Footer';
import WhatsAppWidget from '../components/WhatsAppWidget';
import { blogPosts } from '../data/blogData';
import blogFaqs from '../data/blogFaqs.json';
import { Clock, Calendar, ArrowLeft, Share2 } from 'lucide-react';

const BlogPostPage = () => {
  const { slug } = useParams();
  const post = blogPosts.find(p => p.slug === slug);

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  // Get related posts (same category, excluding current)
  const relatedPosts = blogPosts
    .filter(p => p.category === post.category && p.id !== post.id)
    .slice(0, 3);

  // Article schema
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": post.title,
    "description": post.excerpt,
    "image": post.image,
    "datePublished": post.date,
    "dateModified": post.dateModified || post.date,
    "author": {
      "@type": "Organization",
      "name": "Harpalyk Engineering"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Harpalyk Engineering",
      "logo": {
        "@type": "ImageObject",
        "url": "https://harpalyk.com/logo.png"
      }
    }
  };

  const postFaqs = blogFaqs[post.slug];
  const faqSchema = postFaqs && {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": postFaqs.map((f) => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": { "@type": "Answer", "text": f.a }
    }))
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Harpalyk Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://harpalyk.com/blog/${post.slug}`} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:image" content={post.image} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>}
      </Helmet>

      <Header />

      <main className="pt-20">
        <Breadcrumbs items={[{ label: 'Blog', href: '/blog' }, { label: post.title }]} />
        {/* Hero */}
        <section className="relative h-[400px] overflow-hidden">
          <img 
            src={post.image} 
            alt={post.title}
            fetchpriority="high"
            decoding="async"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 to-gray-900/30" />
          <div className="absolute bottom-0 left-0 right-0 p-8 lg:p-12">
            <div className="max-w-4xl mx-auto">
              <span className="bg-emerald-500 text-white px-3 py-1 rounded-full text-sm font-medium">
                {post.category}
              </span>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mt-4 mb-4">
                {post.title}
              </h1>
              <div className="flex items-center gap-4 text-gray-300 text-sm">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {post.date}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-4 h-4" /> {post.readTime}
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 px-6 lg:px-12">
          <div className="max-w-4xl mx-auto">
            {/* Back link */}
            <Link to="/blog" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8">
              <ArrowLeft className="w-4 h-4" /> Back to Blog
            </Link>

            {/* Author byline (EEAT) */}
            <div className="flex items-center gap-4 bg-gray-50 border border-gray-100 rounded-2xl p-5 mb-10" data-testid="author-byline">
              <div className="w-12 h-12 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold text-lg flex-shrink-0">H</div>
              <div>
                <p className="font-semibold text-gray-900">Harpalyk Editorial Team</p>
                <p className="text-sm text-gray-500">Robotics &amp; smart-home cleaning experts · Reviewed by Harpalyk Engineering</p>
                <p className="text-xs text-gray-400 mt-0.5">Published {post.date} · Last updated {post.dateModified || post.date}</p>
              </div>
            </div>

            {/* Article content */}
            <article 
              className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-a:text-emerald-600 prose-ul:text-gray-600 prose-ol:text-gray-600 prose-li:marker:text-emerald-500"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
            />

            {/* Share */}
            <div className="mt-12 pt-8 border-t border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-gray-600">Share this article:</span>
                <div className="flex gap-4">
                  <a 
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(post.title)}&url=${encodeURIComponent(`https://harpalyk.com/blog/${post.slug}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-500"
                  >
                    Twitter
                  </a>
                  <a 
                    href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(`https://harpalyk.com/blog/${post.slug}`)}&title=${encodeURIComponent(post.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-blue-700"
                  >
                    LinkedIn
                  </a>
                  <a 
                    href={`https://wa.me/?text=${encodeURIComponent(`${post.title} https://harpalyk.com/blog/${post.slug}`)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-500 hover:text-green-600"
                  >
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12 px-6 lg:px-12 bg-gray-50">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-8">Related Articles</h2>
              <div className="grid md:grid-cols-3 gap-8">
                {relatedPosts.map((relatedPost) => (
                  <Link 
                    key={relatedPost.id} 
                    to={`/blog/${relatedPost.slug}`}
                    className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-lg transition-all group"
                  >
                    <img 
                      src={relatedPost.image} 
                      alt={relatedPost.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-40 object-cover"
                    />
                    <div className="p-5">
                      <h3 className="font-bold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-2">
                        {relatedPost.title}
                      </h3>
                      <p className="text-gray-500 text-sm mt-2">{relatedPost.readTime}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* CTA */}
        <section className="py-16 px-6 lg:px-12 bg-emerald-600 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
            <p className="text-emerald-100 mb-8">
              Explore our range of robotic cleaning solutions for homes and businesses.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link 
                to="/#products" 
                className="bg-white text-emerald-700 px-8 py-4 rounded-lg font-bold hover:bg-emerald-100 transition-all"
              >
                View Products
              </Link>
              <Link 
                to="/#contact" 
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold hover:bg-white/10 transition-all"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppWidget />
    </>
  );
};

export default BlogPostPage;
